/*!
 * Copyright 2019 Fondazione Bruno Kessler
 * Licensed under the EUPL, Version 1.1 or - as soon they will be approved by the European
 * Commission - subsequent versions of the EUPL (the "Licence"); You may not use this work
 * except in compliance with the Licence.
 *
 * You may obtain a copy of the Licence at:
 * http://ec.europa.eu/idabc/eupl.html
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the Licence is distributed on an "AS IS" basis, WITHOUT WARRANTIES OR CONDITIONS OF
 * ANY KIND, either express or implied. See the Licence for the specific language
 * governing permissions and limitations under the Licence.
 */

import {ClinicalDataInfo, ClinicalDataType} from "@trec/api/clinical-data";

export interface ListItem {
  key: string;
  label: string;
}

export const ALARM_TYPE: ListItem[] = [
  {key: "none", label: "Non notificare"},
  {key: "notification", label: "Notifica"},
];

export const ALARM_ANTICIPATION_UNIT: ListItem[] = [
  {key: "month", label: "Mesi"},
  {key: "week", label: "Settimane"},
  {key: "day", label: "Giorni"},
  {key: "hour", label: "Ore"},
  {key: "minute", label: "Minuti"},
];

export const EXAM_PERIODIC_UNIT: ListItem[] = [
  {key: "year", label: "Anni"},
  {key: "month", label: "Mesi"},
  {key: "week", label: "Settimane"},
  {key: "day", label: "Giorni"},
];

export const TASK_CLINICAL_DATA_TYPES: ListItem[] = [
  {key: ClinicalDataType.WEIGHT, label: "Peso"},
  {key: ClinicalDataType.BLOOD_PRESSURE, label: "Pressione sanguigna"},
  {key: ClinicalDataType.GLYCAEMIA, label: "Glicemia"},
];

export const TASK_CLINICAL_DATA_TYPES_CARDIO: ListItem[] = [
  ...TASK_CLINICAL_DATA_TYPES,
  /*{ key: ClinicalDataType.SURVEY, label: "Questionario" },*/
  {
    key: ClinicalDataType.OXYGEN_SATURATION,
    label: ClinicalDataInfo[ClinicalDataType.OXYGEN_SATURATION].translation["it"].singular,
  },
  {
    key: ClinicalDataType.VO2MAX,
    label: ClinicalDataInfo[ClinicalDataType.VO2MAX].translation["it"].singular,
  },
  {
    key: ClinicalDataType.HEART_RATE_VARIABILITY,
    label: ClinicalDataInfo[ClinicalDataType.HEART_RATE_VARIABILITY].translation["it"].singular,
  },
  {
    key: ClinicalDataType.HEART_RATE,
    label: ClinicalDataInfo[ClinicalDataType.HEART_RATE].translation["it"].singular,
  },
  {
    key: ClinicalDataType.STEPS,
    label: ClinicalDataInfo[ClinicalDataType.STEPS].translation["it"].singular,
  },
];

export const TASK_CLINICAL_DATA_TYPES_ONCO: ListItem[] = [
  {key: ClinicalDataType.ASTHENIA, label: "Astenia"},
  {key: ClinicalDataType.FEVER, label: "Febbre"},
  {
    key: ClinicalDataType.SIDE_EFFECTS_ONGOING_THERAPY,
    label: "Effetti collaterali terapia in corso",
  },
  {key: ClinicalDataType.BLOOD_PRESSURE, label: "Pressione sanguigna"},
  {key: ClinicalDataType.HEART_RATE, label: "Frequenza cardiaca"},
  {key: ClinicalDataType.SYMPTOM, label: "Sintomi"},
  {key: ClinicalDataType.BODY_TEMPERATURE, label: "Temperatura corporea"},
  {key: ClinicalDataType.WEIGHT, label: "Peso"},
  {key: ClinicalDataType.NOTE, label: "Note"},
];


export const TASK_CLINICAL_DATA_TYPES_PNEUMO: ListItem[] = [
  {key: ClinicalDataType.COUGH_FEVER_PRESENCE, label: "Presenza di tosse e febbre"},
  {key: ClinicalDataType.HEART_RATE, label: "Frequenza cardiaca"},
  {key: ClinicalDataType.SPUTUM_CHANGE, label: "Cambiamento qualità e quantità escreato"},
  {key: ClinicalDataType.DYSPNEA, label: "Dispnea"},
  {key: ClinicalDataType.NOTE, label: "Note"},
  {key: ClinicalDataType.WELL_BEING, label: "Benessere"},
  {key: ClinicalDataType.EXERCISE_TOLERANCE, label: "Tolleranza allo sforzo"},
  {key: ClinicalDataType.OXYGEN_SATURATION, label: "Saturazione ossigeno"},
];

export const TASK_CLINICAL_DATA_TYPES_PREGNANCY: ListItem[] = [
  {key: ClinicalDataType.WEIGHT, label: "Peso"},
  {key: ClinicalDataType.BLOOD_PRESSURE, label: "Pressione sanguigna"},
  {key: ClinicalDataType.KICKS, label: "Movimenti fetali"},
  {key: ClinicalDataType.CONTRACTIONS_EPISODE, label: "Contrazioni"},
  {key: ClinicalDataType.NOTE, label: "Note"},
  {key: ClinicalDataType.SYMPTOM, label: "Sintomi"},
];

export const TASK_CLINICAL_DATA_TYPES_PARKINSON: ListItem[] = [
  {key: ClinicalDataType.APPETITE, label: "Appetito"},
  {key: ClinicalDataType.MOOD_DISORDERS, label: "Disturbi dell'umore"},
  {key: ClinicalDataType.FALLS, label: "Cadute"},
  {key: ClinicalDataType.SLEEP_DISORDERS, label: "Disturbi del sonno"},
  {key: ClinicalDataType.LOSS_OF_INTEREST, label: "Perdite di interesse"},
];

export const TASK_CLINICAL_DATA_TYPES_CDCD: ListItem[] = [
  {key: ClinicalDataType.ACTIVITY, label: "Attività fisica"},
  {key: ClinicalDataType.DAILY_LIVING_CAPACITY_IADL, label: "Capacità di vita quotidiana A-IADL"},
  {key: ClinicalDataType.DYSPHAGIA, label: "Disfagia"},
  {key: ClinicalDataType.FALLS, label: "Cadute"},
  {key: ClinicalDataType.PAIN, label: "Dolore riposo"},

  {key: ClinicalDataType.BEHAVIORAL_DISORDER, label: "Sintomi disturbi comportamentali"},
  {key: ClinicalDataType.DAILY_LIVING_CAPACITY_ADL, label: "Capacità di vita quotidiana ADL"},
  {key: ClinicalDataType.ELIMINATION_ASPECTS_BOWEL, label: "Eliminazione intestinale"},
  {key: ClinicalDataType.GLYCAEMIA, label: "Glicemia"},
  {
    key: ClinicalDataType.SIDE_EFFECTS_ONGOING_THERAPY,
    label: "Effetti collaterali terapia in corso",
  },
  {key: ClinicalDataType.BLOOD_PRESSURE, label: "Pressione sanguigna"},
  {key: ClinicalDataType.DAILY_LIVING_CAPACITY_MBI, label: "Capacità di vita quotidiana MBI"},
  {key: ClinicalDataType.ELIMINATION_ASPECTS_DIURESIS, label: "Eliminazione urinaria"},
  {key: ClinicalDataType.WEIGHT, label: "Peso"},
  {key: ClinicalDataType.HEART_RATE, label: "Frequenza cardiaca"},
];

export const TASK_CLINICAL_DATA_TYPES_PERIOPERATIVO: ListItem[] = [
  {key: ClinicalDataType.ABSTINENCE_SMOKING_ALCOHOL, label: "Astensione da fumo ed alcol"},
  {key: ClinicalDataType.FEVER, label: "Febbre"},
  {key: ClinicalDataType.PAIN, label: "Dolore riposo"},
  {key: ClinicalDataType.VOMITING, label: "Vomito"},
  {key: ClinicalDataType.BOWEL_MOVEMENT, label: "Alvo"},
  {key: ClinicalDataType.MOVEMENT, label: "Movimento"},
  {key: ClinicalDataType.PHYSICAL_ACTIVITY_OBSERVATION, label: "Attività fisica"},
  {key: ClinicalDataType.WEIGHT, label: "Peso"},
  {key: ClinicalDataType.DIET, label: "Dieta"},
  {key: ClinicalDataType.NAUSEA, label: "Nausea"},
  {key: ClinicalDataType.SUPPLEMENT_INTAKE, label: "Assunzione integratori"},

];

export const TASK_CLINICAL_DATA_TYPES_WOUNDCARE: ListItem[] = [
  //{key: ClinicalDataType.BMI, label: "BMI"},
  //{key: ClinicalDataType.DEVICEADHESIONPROBLEMS, label: "Problemi adesione presidio"},
  //{key: ClinicalDataType.PERISTOMALSKIN, label: "Cute peristomale"},
  //{key: ClinicalDataType.STOMA, label: "Stoma"},
  //{key: ClinicalDataType.STOOLTYPE, label: "Tipologia feci"},
  //{key: ClinicalDataType.BOWELMOVEMENTPERDAY, label: "N. evacuazioni/die"},
  {key: ClinicalDataType.EXUDATEPRESENCE, label: "Presenza essudato"},
  {key: ClinicalDataType.MOVEMENT, label: "Movimento"},
  {key: ClinicalDataType.FEVER, label: "Febbre"},
  {key: ClinicalDataType.PAIN, label: "Dolore riposo"},
  {key: ClinicalDataType.MOBILITYLEVEL, label: "Livello mobilità"},
];

export const TASK_CLINICAL_DATA_TYPES_STOMACHCARE: ListItem[] = [
  //{key: ClinicalDataType.FEVER, label: "Febbre"},
  //{key: ClinicalDataType.EXUDATEPRESENCE, label: "Presenza essudato"},
  //{key: ClinicalDataType.MOVEMENT, label: "Movimento"},
  //{key: ClinicalDataType.MOBILITYLEVEL, label: "Livello mobilità"},
  {key: ClinicalDataType.BMI, label: "BMI"},
  {key: ClinicalDataType.PERISTOMALSKIN, label: "Cute peristomale"},
  {key: ClinicalDataType.BOWELMOVEMENTPERDAY, label: "N. evacuazioni/die"},
  {key: ClinicalDataType.STOMA, label: "Stoma"},
  {key: ClinicalDataType.DEVICEADHESIONPROBLEMS, label: "Problemi adesione presidio"},
  {key: ClinicalDataType.STOOLTYPE, label: "Tipologia feci"}

];
